<!-- RegisterAdvertisementModal.vue -->
<template>
  <div class="modal fade" id="registerAdvertModal" tabindex="-1" aria-labelledby="registerAdvertModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="registerAdvertModalLabel">
            {{ isEditMode ? 'Edit Advertisement' : 'Register New Advertisement' }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <div class="modal-body">
          <!-- Progress Steps -->
          <div class="row mb-4">
            <div class="col-12">
              <div class="step-wizard">
                <ul class="step-wizard-list">
                  <li :class="['step-wizard-item', currentStep >= 1 ? 'current-item' : '']">
                    <span class="progress-count">1</span>
                    <span class="progress-label">Advertisement Details</span>
                  </li>
                  <li :class="['step-wizard-item', currentStep >= 2 ? 'current-item' : '']">
                    <span class="progress-count">2</span>
                    <span class="progress-label">Location & Duration</span>
                  </li>
                  <li :class="['step-wizard-item', currentStep >= 3 ? 'current-item' : '']">
                    <span class="progress-count">3</span>
                    <span class="progress-label">Client Information</span>
                  </li>
                  <li :class="['step-wizard-item', currentStep >= 4 ? 'current-item' : '']">
                    <span class="progress-count">4</span>
                    <span class="progress-label">Payment & Review</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Step 1: Advertisement Details -->
          <div v-show="currentStep === 1" class="step-content">
            <h6 class="mb-3">Advertisement Details</h6>
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Advertisement Name <span class="text-danger">*</span></label>
                <input v-model="formData.name" type="text" class="form-control"
                       :class="{'is-invalid': errors.name}"
                       placeholder="Enter advertisement name">
                <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Advertisement Type <span class="text-danger">*</span></label>
                <select v-model="formData.type" class="form-select"
                        :class="{'is-invalid': errors.type}"
                        @change="calculateCharges">
                  <option value="">Select advertisement type</option>
                  <option value="BILLBOARD">Billboard</option>
                  <option value="SIGNAGE">Signage</option>
                  <option value="MOBILE">Mobile Advertisement</option>
                  <option value="POSTER">Poster/Flyer</option>
                  <option value="BANNER">Banner</option>
                </select>
                <div v-if="errors.type" class="invalid-feedback">{{ errors.type }}</div>
              </div>

              <div class="col-md-12">
                <label class="form-label">Description <span class="text-danger">*</span></label>
                <textarea v-model="formData.description" class="form-control"
                          :class="{'is-invalid': errors.description}"
                          rows="3" placeholder="Enter advertisement description"></textarea>
                <div v-if="errors.description" class="invalid-feedback">{{ errors.description }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Size/Dimensions <span class="text-danger">*</span></label>
                <input v-model="formData.size" type="text" class="form-control"
                       :class="{'is-invalid': errors.size}"
                       placeholder="e.g., 48 x 12 feet">
                <div v-if="errors.size" class="invalid-feedback">{{ errors.size }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Advertisement Image/Design</label>
                <input type="file" class="form-control" @change="handleFileUpload" accept="image/*">
                <small class="text-muted">Upload advertisement design or photo (Max 5MB)</small>
              </div>

              <div v-if="formData.imagePreview" class="col-md-12">
                <label class="form-label">Image Preview</label>
                <div class="position-relative d-inline-block">
                  <img :src="formData.imagePreview" class="img-thumbnail" style="max-height: 200px;">
                  <button type="button" class="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                          @click="removeImage">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Step 2: Location & Duration -->
          <div v-show="currentStep === 2" class="step-content">
            <h6 class="mb-3">Location & Duration</h6>
            <div class="row g-3">
              <div class="col-md-4">
                <label class="form-label">Sub County <span class="text-danger">*</span></label>
                <select v-model="formData.subCounty" class="form-select"
                        :class="{'is-invalid': errors.subCounty}"
                        @change="updateWards">
                  <option value="">Select sub county</option>
                  <option v-for="subCounty in subCounties" :key="subCounty" :value="subCounty">
                    {{ subCounty }}
                  </option>
                </select>
                <div v-if="errors.subCounty" class="invalid-feedback">{{ errors.subCounty }}</div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Ward <span class="text-danger">*</span></label>
                <select v-model="formData.ward" class="form-select"
                        :class="{'is-invalid': errors.ward}"
                        :disabled="!formData.subCounty">
                  <option value="">Select ward</option>
                  <option v-for="ward in availableWards" :key="ward" :value="ward">
                    {{ ward }}
                  </option>
                </select>
                <div v-if="errors.ward" class="invalid-feedback">{{ errors.ward }}</div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Specific Location <span class="text-danger">*</span></label>
                <input v-model="formData.specificLocation" type="text" class="form-control"
                       :class="{'is-invalid': errors.specificLocation}"
                       placeholder="e.g., Near Shell Petrol Station">
                <div v-if="errors.specificLocation" class="invalid-feedback">{{ errors.specificLocation }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">GPS Coordinates (Optional)</label>
                <div class="input-group">
                  <input v-model="formData.gpsCoordinates" type="text" class="form-control"
                         placeholder="e.g., -1.3667, 38.0100">
                  <button class="btn btn-outline-secondary" type="button" @click="getCurrentLocation">
                    <i class="mdi mdi-crosshairs-gps"></i> Get Current Location
                  </button>
                </div>
                <small class="text-muted">Click the button to auto-detect your current location</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">
                  <i class="mdi mdi-map-marker text-danger"></i> Location on Map
                </label>
                <div id="location-map" ref="locationMap" style="height: 200px; border-radius: 4px;"></div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Start Date <span class="text-danger">*</span></label>
                <input v-model="formData.startDate" type="date" class="form-control"
                       :class="{'is-invalid': errors.startDate}"
                       :min="minDate" @change="calculateCharges">
                <div v-if="errors.startDate" class="invalid-feedback">{{ errors.startDate }}</div>
              </div>

              <div class="col-md-4">
                <label class="form-label">End Date <span class="text-danger">*</span></label>
                <input v-model="formData.endDate" type="date" class="form-control"
                       :class="{'is-invalid': errors.endDate}"
                       :min="formData.startDate || minDate" @change="calculateCharges">
                <div v-if="errors.endDate" class="invalid-feedback">{{ errors.endDate }}</div>
              </div>

              <div class="col-md-4">
                <label class="form-label">Duration</label>
                <input :value="duration" type="text" class="form-control" readonly>
                <small class="text-muted">Calculated from dates</small>
              </div>
            </div>
          </div>

          <!-- Step 3: Client Information -->
          <div v-show="currentStep === 3" class="step-content">
            <h6 class="mb-3">Client Information</h6>

            <!-- Client Search -->
            <div class="row mb-3">
              <div class="col-md-12">
                <label class="form-label">Search Existing Client</label>
                <div class="input-group">
                  <input v-model="clientSearch" type="text" class="form-control"
                         placeholder="Search by client name, phone, or email"
                         @input="searchClients">
                  <button class="btn btn-outline-secondary" type="button" @click="clearClientSearch">
                    <i class="mdi mdi-close"></i> Clear
                  </button>
                </div>
                <!-- Search Results -->
                <div v-if="searchResults.length > 0" class="list-group mt-2">
                  <button v-for="client in searchResults" :key="client.id"
                          type="button" class="list-group-item list-group-item-action"
                          @click="selectClient(client)">
                    <div class="d-flex w-100 justify-content-between">
                      <h6 class="mb-1">{{ client.name }}</h6>
                      <small>{{ client.businessType }}</small>
                    </div>
                    <p class="mb-1 small">
                      <i class="mdi mdi-phone me-1"></i>{{ client.phone }} |
                      <i class="mdi mdi-email me-1"></i>{{ client.email }}
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <hr class="my-3">

            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label">Client/Company Name <span class="text-danger">*</span></label>
                <input v-model="formData.client.name" type="text" class="form-control"
                       :class="{'is-invalid': errors['client.name']}">
                <div v-if="errors['client.name']" class="invalid-feedback">{{ errors['client.name'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Business Type <span class="text-danger">*</span></label>
                <select v-model="formData.client.businessType" class="form-select"
                        :class="{'is-invalid': errors['client.businessType']}">
                  <option value="">Select business type</option>
                  <option value="Telecommunications">Telecommunications</option>
                  <option value="Financial Services">Financial Services</option>
                  <option value="Beverage Manufacturing">Beverage Manufacturing</option>
                  <option value="Food Manufacturing">Food Manufacturing</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Retail">Retail</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Education">Education</option>
                  <option value="Other">Other</option>
                </select>
                <div v-if="errors['client.businessType']" class="invalid-feedback">{{ errors['client.businessType'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Contact Person <span class="text-danger">*</span></label>
                <input v-model="formData.client.contactPerson" type="text" class="form-control"
                       :class="{'is-invalid': errors['client.contactPerson']}">
                <div v-if="errors['client.contactPerson']" class="invalid-feedback">{{ errors['client.contactPerson'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                <input v-model="formData.client.phone" type="tel" class="form-control"
                       :class="{'is-invalid': errors['client.phone']}"
                       placeholder="+254XXXXXXXXX">
                <div v-if="errors['client.phone']" class="invalid-feedback">{{ errors['client.phone'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Email Address <span class="text-danger">*</span></label>
                <input v-model="formData.client.email" type="email" class="form-control"
                       :class="{'is-invalid': errors['client.email']}">
                <div v-if="errors['client.email']" class="invalid-feedback">{{ errors['client.email'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Business Address <span class="text-danger">*</span></label>
                <input v-model="formData.client.address" type="text" class="form-control"
                       :class="{'is-invalid': errors['client.address']}">
                <div v-if="errors['client.address']" class="invalid-feedback">{{ errors['client.address'] }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">KRA PIN (Optional)</label>
                <input v-model="formData.client.kraPin" type="text" class="form-control"
                       placeholder="e.g., A000000000X">
              </div>

              <div class="col-md-6">
                <label class="form-label">Business Registration Number (Optional)</label>
                <input v-model="formData.client.businessRegNumber" type="text" class="form-control">
              </div>
            </div>
          </div>

          <!-- Step 4: Payment & Review -->
          <div v-show="currentStep === 4" class="step-content">
            <h6 class="mb-3">Payment Details & Review</h6>

            <!-- Fee Breakdown -->
            <div class="card mb-3">
              <div class="card-header bg-light">
                <h6 class="mb-0">Fee Calculation</h6>
              </div>
              <div class="card-body">
                <div class="row mb-2">
                  <div class="col-6">Base Rate ({{ formData.type }}):</div>
                  <div class="col-6 text-end">{{ formatPrice(charges.baseRate) }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-6">Location Multiplier ({{ formData.subCounty }}):</div>
                  <div class="col-6 text-end">x{{ charges.locationMultiplier }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-6">Size Multiplier:</div>
                  <div class="col-6 text-end">x{{ charges.sizeMultiplier }}</div>
                </div>
                <div class="row mb-2">
                  <div class="col-6">Duration ({{ paymentPeriod }} months):</div>
                  <div class="col-6 text-end">x{{ paymentPeriod }}</div>
                </div>
                <div v-if="charges.durationDiscount > 0" class="row mb-2 text-success">
                  <div class="col-6">Long-term Discount:</div>
                  <div class="col-6 text-end">-{{ charges.durationDiscount * 100 }}%</div>
                </div>
                <hr>
                <div class="row">
                  <div class="col-6"><strong>Total Amount:</strong></div>
                  <div class="col-6 text-end"><strong>{{ formatPrice(totalAmount) }}</strong></div>
                </div>
              </div>
            </div>

            <!-- Payment Options -->
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <label class="form-label">Payment Method <span class="text-danger">*</span></label>
                <select v-model="formData.paymentMethod" class="form-select"
                        :class="{'is-invalid': errors.paymentMethod}">
                  <option value="">Select payment method</option>
                  <option value="CASH">Cash</option>
                  <option value="BANK_TRANSFER">Bank Transfer</option>
                  <option value="MPESA">M-Pesa</option>
                  <option value="CHEQUE">Cheque</option>
                </select>
                <div v-if="errors.paymentMethod" class="invalid-feedback">{{ errors.paymentMethod }}</div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Payment Status <span class="text-danger">*</span></label>
                <select v-model="formData.paymentStatus" class="form-select"
                        :class="{'is-invalid': errors.paymentStatus}">
                  <option value="PENDING">Pending Payment</option>
                  <option value="PAID">Paid</option>
                  <option value="PARTIAL">Partial Payment</option>
                </select>
                <div v-if="errors.paymentStatus" class="invalid-feedback">{{ errors.paymentStatus }}</div>
              </div>

              <div v-if="formData.paymentStatus === 'PARTIAL'" class="col-md-6">
                <label class="form-label">Amount Paid</label>
                <input v-model.number="formData.amountPaid" type="number" class="form-control" min="0">
              </div>
            </div>

            <!-- Summary Review -->
            <div class="card">
              <div class="card-header bg-light">
                <h6 class="mb-0">Advertisement Summary</h6>
              </div>
              <div class="card-body">
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="text-muted small">Advertisement Details</label>
                      <div><strong>{{ formData.name }}</strong></div>
                      <div class="small">{{ formData.type }} - {{ formData.size }}</div>
                      <div class="small text-muted">{{ formData.description }}</div>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small">Location</label>
                      <div>{{ formData.specificLocation }}</div>
                      <div class="small">{{ formData.ward }}, {{ formData.subCounty }}</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="text-muted small">Client</label>
                      <div><strong>{{ formData.client.name }}</strong></div>
                      <div class="small">{{ formData.client.contactPerson }}</div>
                      <div class="small">{{ formData.client.phone }} | {{ formData.client.email }}</div>
                    </div>
                    <div class="mb-3">
                      <label class="text-muted small">Duration & Payment</label>
                      <div>{{ formData.startDate }} to {{ formData.endDate }}</div>
                      <div class="small">{{ paymentPeriod }} months - {{ formatPrice(totalAmount) }}</div>
                      <div class="small">
                        <span :class="formData.paymentStatus === 'PAID' ? 'text-success' : 'text-warning'">
                          {{ formData.paymentStatus }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Terms and Conditions -->
            <div class="form-check mt-3">
              <input v-model="formData.acceptTerms" class="form-check-input" type="checkbox" id="acceptTerms">
              <label class="form-check-label" for="acceptTerms">
                I confirm that all the information provided is accurate and I accept the terms and conditions
                for outdoor advertisement in Kitui County.
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div>
              <button v-if="currentStep > 1" type="button" class="btn btn-secondary" @click="previousStep">
                <i class="mdi mdi-arrow-left"></i> Previous
              </button>
            </div>
            <div>
              <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
              <button v-if="currentStep < 4" type="button" class="btn btn-primary" @click="nextStep">
                Next <i class="mdi mdi-arrow-right"></i>
              </button>
              <button v-if="currentStep === 4" type="button" class="btn btn-success"
                      @click="submitAdvertisement" :disabled="!formData.acceptTerms || submitting">
                <span v-if="!submitting">
                  <i class="mdi mdi-check-circle"></i> {{ isEditMode ? 'Update' : 'Register' }} Advertisement
                </span>
                <span v-else>
                  <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                  Processing...
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import moment from 'moment';

export default {
  name: 'RegisterAdvertisementModal',
  props: {
    advertisement: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      currentStep: 1,
      submitting: false,
      clientSearch: '',
      searchResults: [],
      locationMap: null,
      locationMarker: null,

      formData: {
        name: '',
        type: '',
        description: '',
        size: '',
        imagePreview: '',
        imageFile: null,
        subCounty: '',
        ward: '',
        specificLocation: '',
        gpsCoordinates: '',
        startDate: '',
        endDate: '',
        client: {
          name: '',
          businessType: '',
          contactPerson: '',
          phone: '',
          email: '',
          address: '',
          kraPin: '',
          businessRegNumber: ''
        },
        paymentMethod: '',
        paymentStatus: 'PENDING',
        amountPaid: 0,
        acceptTerms: false
      },

      charges: {
        baseRate: 0,
        locationMultiplier: 1,
        sizeMultiplier: 1,
        durationDiscount: 0
      },

      errors: {},

      // Location data
      subCounties: [
        'Kitui Central', 'Kitui West', 'Kitui East', 'Kitui South',
        'Mwingi North', 'Mwingi West', 'Mwingi Central'
      ],

      wardsBySubCounty: {
        'Kitui Central': ['Township', 'Kyangwithya East', 'Kyangwithya West', 'Miambani', 'Mulango'],
        'Kitui West': ['Mutonguni', 'Kauwi', 'Matinyani', 'Kwavonza-Yatta'],
        'Kitui East': ['Mutito-Kaliku', 'Voo-Kyamatu', 'Endui-Malalani', 'Nzambani'],
        'Kitui South': ['Ikutha', 'Kanziko', 'Athi', 'Mutomo', 'Ikanga-Kyatune', 'Mutha'],
        'Mwingi North': ['Kyuso', 'Tseikuru', 'Ngomeni', 'Tharaka', 'Mumoni'],
        'Mwingi West': ['Migwani', 'Kiomo-Kyethani', 'Waita'],
        'Mwingi Central': ['Central', 'Kivou', 'Ngutani', 'Nuu', 'Mui']
      },

      // Base rates by advertisement type
      baseRates: {
        'BILLBOARD': 25000,
        'SIGNAGE': 15000,
        'MOBILE': 20000,
        'POSTER': 3000,
        'BANNER': 12000
      },

      // Location multipliers
      locationMultipliers: {
        'Kitui Central': 1.5,
        'Kitui West': 1.2,
        'Kitui East': 1.0,
        'Kitui South': 1.0,
        'Mwingi North': 0.8,
        'Mwingi West': 0.8,
        'Mwingi Central': 0.9
      },

      // Mock client database for search
      existingClients: [
        {
          id: 1,
          name: "Safaricom PLC",
          phone: "+254722000000",
          email: "marketing@safaricom.co.ke",
          businessType: "Telecommunications",
          contactPerson: "Jane Mwangi",
          address: "Safaricom House, Nairobi"
        },
        {
          id: 2,
          name: "Equity Bank Kenya Limited",
          phone: "+254711049000",
          email: "marketing@equitybank.co.ke",
          businessType: "Financial Services",
          contactPerson: "Peter Kiprotich",
          address: "Equity Centre, Nairobi"
        }
      ]
    };
  },
  computed: {
    isEditMode() {
      return this.advertisement !== null;
    },

    minDate() {
      return moment().format('YYYY-MM-DD');
    },

    availableWards() {
      return this.wardsBySubCounty[this.formData.subCounty] || [];
    },

    duration() {
      if (this.formData.startDate && this.formData.endDate) {
        const start = moment(this.formData.startDate);
        const end = moment(this.formData.endDate);
        const months = end.diff(start, 'months');
        const days = end.diff(start.add(months, 'months'), 'days');
        return `${months} months${days > 0 ? ` and ${days} days` : ''}`;
      }
      return '0 months';
    },

    paymentPeriod() {
      if (this.formData.startDate && this.formData.endDate) {
        const start = moment(this.formData.startDate);
        const end = moment(this.formData.endDate);
        return Math.ceil(end.diff(start, 'months', true));
      }
      return 0;
    },

    totalAmount() {
      const base = this.charges.baseRate;
      const location = this.charges.locationMultiplier;
      const size = this.charges.sizeMultiplier;
      const months = this.paymentPeriod;
      const discount = this.charges.durationDiscount;

      let total = base * location * size * months;
      if (discount > 0) {
        total = total * (1 - discount);
      }

      return Math.round(total);
    }
  },
  watch: {
    advertisement: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadAdvertisementData(newVal);
        } else {
          this.resetForm();
        }
      }
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0
      }).format(value);
    },

    loadAdvertisementData(ad) {
      this.formData = {
        ...this.formData,
        name: ad.name,
        type: ad.type,
        description: ad.description,
        size: ad.size,
        imagePreview: ad.image,
        subCounty: ad.subCounty,
        ward: ad.ward,
        specificLocation: ad.specificLocation,
        gpsCoordinates: ad.gpsCoordinates,
        startDate: ad.startDate,
        endDate: ad.endDate,
        client: { ...ad.client },
        paymentMethod: ad.paymentMethod || '',
        paymentStatus: ad.paymentStatus
      };
      this.calculateCharges();
    },

    resetForm() {
      this.currentStep = 1;
      this.formData = {
        name: '',
        type: '',
        description: '',
        size: '',
        imagePreview: '',
        imageFile: null,
        subCounty: '',
        ward: '',
        specificLocation: '',
        gpsCoordinates: '',
        startDate: '',
        endDate: '',
        client: {
          name: '',
          businessType: '',
          contactPerson: '',
          phone: '',
          email: '',
          address: '',
          kraPin: '',
          businessRegNumber: ''
        },
        paymentMethod: '',
        paymentStatus: 'PENDING',
        amountPaid: 0,
        acceptTerms: false
      };
      this.errors = {};
      this.clientSearch = '';
      this.searchResults = [];
    },

    validateStep(step) {
      this.errors = {};
      let isValid = true;

      switch (step) {
        case 1:
          if (!this.formData.name) {
            this.errors.name = 'Advertisement name is required';
            isValid = false;
          }
          if (!this.formData.type) {
            this.errors.type = 'Advertisement type is required';
            isValid = false;
          }
          if (!this.formData.description) {
            this.errors.description = 'Description is required';
            isValid = false;
          }
          if (!this.formData.size) {
            this.errors.size = 'Size/dimensions are required';
            isValid = false;
          }
          break;

        case 2:
          if (!this.formData.subCounty) {
            this.errors.subCounty = 'Sub County is required';
            isValid = false;
          }
          if (!this.formData.ward) {
            this.errors.ward = 'Ward is required';
            isValid = false;
          }
          if (!this.formData.specificLocation) {
            this.errors.specificLocation = 'Specific location is required';
            isValid = false;
          }
          if (!this.formData.startDate) {
            this.errors.startDate = 'Start date is required';
            isValid = false;
          }
          if (!this.formData.endDate) {
            this.errors.endDate = 'End date is required';
            isValid = false;
          }
          if (this.formData.startDate && this.formData.endDate) {
            const start = moment(this.formData.startDate);
            const end = moment(this.formData.endDate);
            if (end.isBefore(start)) {
              this.errors.endDate = 'End date must be after start date';
              isValid = false;
            }
          }
          break;

        case 3:
          if (!this.formData.client.name) {
            this.errors['client.name'] = 'Client name is required';
            isValid = false;
          }
          if (!this.formData.client.businessType) {
            this.errors['client.businessType'] = 'Business type is required';
            isValid = false;
          }
          if (!this.formData.client.contactPerson) {
            this.errors['client.contactPerson'] = 'Contact person is required';
            isValid = false;
          }
          if (!this.formData.client.phone) {
            this.errors['client.phone'] = 'Phone number is required';
            isValid = false;
          } else if (!/^(\+254|0)[1-9]\d{8}$/.test(this.formData.client.phone)) {
            this.errors['client.phone'] = 'Invalid phone number format';
            isValid = false;
          }
          if (!this.formData.client.email) {
            this.errors['client.email'] = 'Email is required';
            isValid = false;
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.client.email)) {
            this.errors['client.email'] = 'Invalid email format';
            isValid = false;
          }
          if (!this.formData.client.address) {
            this.errors['client.address'] = 'Business address is required';
            isValid = false;
          }
          break;

        case 4:
          if (!this.formData.paymentMethod) {
            this.errors.paymentMethod = 'Payment method is required';
            isValid = false;
          }
          if (!this.formData.paymentStatus) {
            this.errors.paymentStatus = 'Payment status is required';
            isValid = false;
          }
          break;
      }

      return isValid;
    },

    nextStep() {
      if (this.validateStep(this.currentStep)) {
        this.currentStep++;
        if (this.currentStep === 2) {
          this.$nextTick(() => {
            this.initializeLocationMap();
          });
        }
      }
    },

    previousStep() {
      this.currentStep--;
    },

    updateWards() {
      this.formData.ward = '';
      this.calculateCharges();
    },

    calculateCharges() {
      // Base rate
      this.charges.baseRate = this.baseRates[this.formData.type] || 0;

      // Location multiplier
      this.charges.locationMultiplier = this.locationMultipliers[this.formData.subCounty] || 1;

      // Size multiplier (simplified logic)
      if (this.formData.type === 'BILLBOARD') {
        this.charges.sizeMultiplier = 2.0;
      } else if (this.formData.type === 'MOBILE') {
        this.charges.sizeMultiplier = 1.5;
      } else if (this.formData.type === 'POSTER') {
        this.charges.sizeMultiplier = 0.8;
      } else {
        this.charges.sizeMultiplier = 1.2;
      }

      // Duration discount
      const months = this.paymentPeriod;
      if (months >= 12) {
        this.charges.durationDiscount = 0.20;
      } else if (months >= 6) {
        this.charges.durationDiscount = 0.10;
      } else if (months >= 3) {
        this.charges.durationDiscount = 0.05;
      } else {
        this.charges.durationDiscount = 0;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('File size should not exceed 5MB');
          return;
        }

        this.formData.imageFile = file;
        const reader = new FileReader();
        reader.onload = (e) => {
          this.formData.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    removeImage() {
      this.formData.imageFile = null;
      this.formData.imagePreview = '';
    },

    searchClients() {
      const search = this.clientSearch.toLowerCase();
      if (search.length < 2) {
        this.searchResults = [];
        return;
      }

      this.searchResults = this.existingClients.filter(client =>
          client.name.toLowerCase().includes(search) ||
          client.phone.includes(search) ||
          client.email.toLowerCase().includes(search)
      );
    },

    selectClient(client) {
      this.formData.client = { ...client };
      this.clientSearch = '';
      this.searchResults = [];
    },

    clearClientSearch() {
      this.clientSearch = '';
      this.searchResults = [];
      this.formData.client = {
        name: '',
        businessType: '',
        contactPerson: '',
        phone: '',
        email: '',
        address: '',
        kraPin: '',
        businessRegNumber: ''
      };
    },

    initializeLocationMap() {
      if (!this.$refs.locationMap) return;

      // Initialize map
      this.locationMap = L.map(this.$refs.locationMap).setView([-1.3667, 38.0100], 10);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.locationMap);

      // Add click handler
      this.locationMap.on('click', (e) => {
        this.setMapLocation(e.latlng.lat, e.latlng.lng);
      });

      // If coordinates exist, show marker
      if (this.formData.gpsCoordinates) {
        const [lat, lng] = this.formData.gpsCoordinates.split(',').map(c => parseFloat(c.trim()));
        this.setMapLocation(lat, lng);
      }
    },

    setMapLocation(lat, lng) {
      // Remove existing marker
      if (this.locationMarker) {
        this.locationMap.removeLayer(this.locationMarker);
      }

      // Add new marker
      this.locationMarker = L.marker([lat, lng]).addTo(this.locationMap);
      this.locationMap.setView([lat, lng], 13);

      // Update coordinates
      this.formData.gpsCoordinates = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    },

    getCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              this.setMapLocation(lat, lng);
            },
            (error) => {
              alert('Unable to retrieve your location. Please enter coordinates manually.');
            }
        );
      } else {
        alert('Geolocation is not supported by your browser.');
      }
    },

    async submitAdvertisement() {
      if (!this.validateStep(4) || !this.formData.acceptTerms) {
        return;
      }

      this.submitting = true;

      try {
        // Prepare data for submission
        const advertisementData = {
          ...this.formData,
          totalAmount: this.totalAmount,
          paymentPeriod: this.paymentPeriod,
          charges: { ...this.charges },
          status: this.formData.paymentStatus === 'PAID' ? 'ACTIVE' : 'PENDING',
          advertisementId: this.generateAdvertisementId()
        };

        // Here you would make an API call to save the advertisement
        // For now, we'll emit an event
        this.$emit('advertisement-saved', advertisementData);

        // Close modal
        const modal = bootstrap.Modal.getInstance(document.getElementById('registerAdvertModal'));
        modal.hide();

        // Show success message
        this.$emit('show-message', {
          type: 'success',
          message: `Advertisement "${advertisementData.name}" has been ${this.isEditMode ? 'updated' : 'registered'} successfully!`
        });

        // Reset form
        this.resetForm();
      } catch (error) {
        this.$emit('show-message', {
          type: 'error',
          message: 'Failed to save advertisement. Please try again.'
        });
      } finally {
        this.submitting = false;
      }
    },

    generateAdvertisementId() {
      const prefix = 'KTU-AD-';
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      return `${prefix}${timestamp}${random}`;
    }
  }
};
</script>

<style scoped>
/* Progress Steps Styling */
.step-wizard {
  padding: 20px 0;
}

.step-wizard-list {
  display: flex;
  justify-content: space-between;
  list-style: none;
  position: relative;
  padding: 0;
  margin: 0;
}

.step-wizard-list::before {
  content: "";
  position: absolute;
  top: 20px;
  left: 10%;
  width: 80%;
  height: 2px;
  background-color: #e9ecef;
  z-index: 1;
}

.step-wizard-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
}

.step-wizard-item .progress-count {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: #e9ecef;
  color: #6c757d;
  font-weight: 600;
  margin-bottom: 8px;
  transition: all 0.3s ease;
}

.step-wizard-item.current-item .progress-count {
  background-color: #0d6efd;
  color: #fff;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
}

.step-wizard-item .progress-label {
  font-size: 0.875rem;
  color: #6c757d;
  text-align: center;
  max-width: 100px;
}

.step-wizard-item.current-item .progress-label {
  color: #0d6efd;
  font-weight: 600;
}

/* Step Content */
.step-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Form Styling */
.form-label {
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.form-control:focus,
.form-select:focus {
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

/* Card Styling */
.card {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
}

/* Search Results */
.list-group-item {
  cursor: pointer;
  transition: all 0.2s ease;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

/* Map Container */
#location-map {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

/* Image Preview */
.img-thumbnail {
  padding: 0.25rem;
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  max-width: 100%;
  height: auto;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .step-wizard-list {
    flex-wrap: wrap;
  }

  .step-wizard-item {
    width: 50%;
    margin-bottom: 20px;
  }

  .step-wizard-list::before {
    display: none;
  }

  .modal-xl {
    max-width: 95%;
  }
}
</style>